import { apiConfig } from '@/config/api';
import { authenticatedFetch, APIError } from '@/lib/api/authenticatedFetch';

export interface DataCollectionTask {
  name: string;
  task_id: string;
  datasource?: string;
  oem: string;
  state: string;
  zone: string;
  zone_slug?: string;
  enabled: boolean;
}

export interface RunTaskRequest {
  task_name?: string;
  zone_slug?: string;
  oem?: string;
  make_name?: string;
  tenant_id?: string; // Tenant ID for multi-tenant execution
  trigger_mode?: 'on_demand' | 'scheduled';
  wait_for_completion?: boolean;
  allow_disabled?: boolean;
  force?: boolean;
  input?: Record<string, unknown>;
}

export interface InventorySummary {
  ingest_batch_id?: string;
  staging_inserted: number;
  snapshot_inserted: number;
  snapshot_updated: number;
  history_inserted: number;
}

export interface RunTaskResponse {
  task: string;
  trigger_mode?: string;
  run_id: string;
  status: string;
  dataset_id?: string | null;
  records?: number | null;
  gcs_path?: string | null;
  finished_at?: string | null;
  started_at?: string | null;
  duration_seconds?: number | null;
  usage?: Record<string, unknown> | null;
  cost_usd?: number | null;
  inventory_summary?: InventorySummary | null;
}

export interface ScheduleTaskRequest extends RunTaskRequest {
  task_name: string; // Required for schedule
  start_at?: string; // ISO timestamp in America/New_York
  cron_expression: string;
  time_zone?: string; // Default: America/New_York
  delete_schedule_after_completion?: boolean; // Default: true
}

export interface ScheduleTaskResponse extends RunTaskResponse {
  schedule_id: string;
}

export interface TaskStatus {
  run_id: string;
  task_name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'SUCCEEDED';
  started_at?: string;
  completed_at?: string;
  records_processed?: number;
  error?: string;
}

export interface ExecutionHistory {
  id?: string; // UUID from database
  run_id: string; // Apify run ID
  workflow_name?: string; // Human-readable workflow name
  task_name: string;
  oem?: string | null;
  zone?: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'SUCCEEDED' | 'queued';
  started_at: string | null;
  finished_at?: string | null; // Maps to database: finished_at (OpenAPI uses finished_at)
  completed_at?: string; // Legacy field for backward compatibility
  duration_seconds?: number | null;
  records?: number | null; // Maps to database: records (OpenAPI uses records)
  records_processed?: number; // Legacy field for backward compatibility
  trigger_mode?: string;
  schedule_id?: string | null; // ID of the schedule that triggered this execution (if scheduled)
  datasource?: string | null;
  state?: string | null;
  errors?: Record<string, unknown> | null;
  // OpenAPI spec fields (from prod.data_collection_run_logs)
  validation_status?: string | null; // Validation status: 'passed', 'failed', or null
  validation_summary?: Record<string, unknown> | null; // Validation details: issues, counts, metadata
  gcs_path?: string | null; // Google Cloud Storage path for the collected dataset
  inventory_summary?: InventorySummary | null; // Summary of inventory operations
  // Input parameters from original execution request
  input_params?: {
    inventory_date?: string; // YYYY-MM-DD format
    zone_slug?: string; // Slug format (e.g., "tampa-bay")
    make_name?: string; // Display format (e.g., "Ford")
    oem?: string; // Lowercase (e.g., "ford")
  } | null;
  // Legacy fields for backward compatibility
  task_id?: string;
  tenant_id?: string;
  error?: string;
  triggered_by?: string;
  triggered_at?: string;
  apify_run_id?: string;
  created_at?: string;
  last_updated?: string;
}

export interface ExecutionLogEntry {
  id?: number; // Maps to database: id (BIGSERIAL)
  log_id?: string; // Maps to database: log_id (UUID, FK to run_logs)
  timestamp: string; // Maps to database: timestamp
  level: 'info' | 'warning' | 'error' | 'success'; // Maps to database: event_type
  event_type?: string; // Raw event_type from database
  message: string;
  details?: Record<string, unknown>; // Maps to database: metadata (JSONB)
  metadata?: Record<string, unknown>; // Raw metadata from database
}

export interface SyncRunStatusRequest {
  sync_from_apify?: boolean;
  status?: string;
  completed_at?: string;
  error?: Record<string, unknown>;
}

export interface SyncRunStatusResponse {
  run_id: string;
  old_status: string;
  new_status: string;
  updated_at: string;
  synced_from_apify?: boolean;
}

export interface CleanupStaleStatusesRequest {
  task_name?: string;
  older_than_hours?: number;
  statuses?: string[];
  sync_from_apify?: boolean;
  dry_run?: boolean;
}

export interface CleanupStaleStatusesResponse {
  total_checked: number;
  runs_updated: number;
  runs_skipped: number;
  details: Array<{
    run_id: string;
    task_name: string;
    old_status: string;
    new_status: string;
    apify_status: string;
    updated: boolean;
  }>;
  // Legacy field for backward compatibility
  stale_runs_found?: number;
}

export interface SyncAllStatusesRequest {
  task_name?: string;
  max_age_hours?: number;
}

export interface SyncAllStatusesResponse {
  job_id: string;
  estimated_runs_to_check: number;
  status: 'queued' | 'running' | 'completed';
  result?: CleanupStaleStatusesResponse;
}

export interface ActiveRunInfo {
  run_id: string;
  status?: string;
  started_at?: string;
  apify_status?: string;
  apify_finished_at?: string;
  is_stale?: boolean;
}

export interface ExistingRunRequest {
  task_name: string;
  run_id?: string; // Specific Apify run identifier to retrieve
  use_last_successful?: boolean; // When true, fetch the most recent successful run for the task
  allow_disabled?: boolean; // Default: true
}

export interface ExistingRunResponse extends RunTaskResponse {}

/**
 * Response from backfill endpoint
 * OpenAPI spec: BackfillResponse
 */
export interface BackfillResponse {
  run_id: string; // Run ID (UUID or Apify run ID)
  log_id: string; // UUID - Database log entry ID
  apify_run_id: string; // Apify run identifier
  message: string; // Status message describing the backfill operation
  result: RunTaskResponse; // Updated run task response with backfilled data
}

class DataCollectionAPI {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = apiConfig.dataCollection.baseUrl;
    this.timeout = apiConfig.dataCollection.timeout;
  }

  /**
   * List all available data collection tasks
   * @param includeDisabled - Include tasks that are currently disabled in configuration (default: true)
   */
  async listTasks(includeDisabled: boolean = true): Promise<DataCollectionTask[]> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const url = new URL(`${this.baseUrl}/tasks`);
    url.searchParams.set('include_disabled', String(includeDisabled));

    const response = await authenticatedFetch(url.toString(), {
      method: 'GET',
      timeout: this.timeout
    });

    const data = await response.json();
    return data.tasks || [];
  }

  /**
   * Trigger a task execution
   */
  async runTask(request: RunTaskRequest): Promise<RunTaskResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/tasks/run`, {
      method: 'POST',
      body: JSON.stringify(request),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Schedule a recurring task
   */
  async scheduleTask(
    taskName: string,
    cronExpression: string,
    timeZone: string = 'America/New_York'
  ): Promise<ScheduleTaskResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const request: ScheduleTaskRequest = {
      task_name: taskName,
      cron_expression: cronExpression,
      time_zone: timeZone
    };

    const response = await authenticatedFetch(`${this.baseUrl}/tasks/schedule`, {
      method: 'POST',
      body: JSON.stringify(request),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Get task execution status
   * Note: This endpoint may need backend implementation
   */
  async getTaskStatus(runId: string): Promise<TaskStatus | null> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    try {
      const response = await authenticatedFetch(`${this.baseUrl}/tasks/${runId}/status`, {
        method: 'GET',
        timeout: this.timeout
      });

      return response.json();
    } catch (error) {
      // If endpoint doesn't exist (404), return null
      if (error instanceof APIError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get execution history for tasks
   * Fetches recent execution history from the backend
   * @param limit - Maximum number of executions to return (1-1000, default: 100)
   * @param filters - Optional filters including schedule_id for schedule-specific history
   */
  async getExecutionHistory(
    limit: number = 100,
    filters?: { dateFrom?: string; status?: string; search?: string; schedule_id?: string }
  ): Promise<ExecutionHistory[]> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    // Validate limit according to OpenAPI spec (1-1000)
    const validatedLimit = Math.max(1, Math.min(1000, limit));

    try {
      const url = new URL(`${this.baseUrl}/executions/history`);
      url.searchParams.set('limit', String(validatedLimit));

      // Add optional filters
      if (filters?.dateFrom) {
        url.searchParams.set('date_from', filters.dateFrom);
      }
      if (filters?.status) {
        url.searchParams.set('status', filters.status);
      }
      if (filters?.search) {
        url.searchParams.set('search', filters.search);
      }
      if (filters?.schedule_id) {
        url.searchParams.set('schedule_id', filters.schedule_id);
      }

      const response = await authenticatedFetch(url.toString(), {
        method: 'GET',
        timeout: this.timeout
      });

      const data = await response.json();
      // OpenAPI spec returns { executions: [], count: integer }
      const executions = data.executions || data.history || [];

      // Normalize field names: finished_at -> completed_at for backward compatibility
      return executions.map((exec: ExecutionHistory) => ({
        ...exec,
        completed_at: exec.finished_at || exec.completed_at,
        records_processed: exec.records || exec.records_processed
      }));
    } catch (error) {
      // If endpoint doesn't exist (404), return empty array
      if (error instanceof APIError && error.status === 404) {
        return [];
      }
      // Handle CORS errors gracefully (shouldn't happen now, but keep for safety)
      if (error instanceof APIError && (error.status === 0 || error.message.includes('CORS'))) {
        // CORS error accessing execution history endpoint
        return [];
      }
      throw error;
    }
  }

  /**
   * Get a single execution by run ID
   * Backend auto-syncs stale Apify status on GET
   */
  async getExecution(runId: string): Promise<ExecutionHistory | null> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    try {
      const response = await authenticatedFetch(`${this.baseUrl}/executions/${runId}`, {
        method: 'GET',
        timeout: this.timeout
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData: ErrorResponse = await response.json();
        throw new APIError(
          errorData.error || 'Failed to get execution',
          response.status,
          response.statusText,
          errorData
        );
      }

      const exec: ExecutionHistory = await response.json();
      return {
        ...exec,
        completed_at: exec.finished_at || exec.completed_at,
        records_processed: exec.records || exec.records_processed
      };
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        return null;
      }
      if (error instanceof APIError && (error.status === 0 || error.message.includes('CORS'))) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get execution logs for a specific run
   */
  async getExecutionLogs(runId: string): Promise<ExecutionLogEntry[]> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    try {
      const response = await authenticatedFetch(`${this.baseUrl}/executions/${runId}/logs`, {
        method: 'GET',
        timeout: this.timeout
      });

      const data = await response.json();
      return data.logs || [];
    } catch (error) {
      // If endpoint doesn't exist (404), return empty array
      if (error instanceof APIError && error.status === 404) {
        return [];
      }
      // Handle CORS errors gracefully (shouldn't happen now, but keep for safety)
      if (error instanceof APIError && (error.status === 0 || error.message.includes('CORS'))) {
        // CORS error accessing execution logs endpoint
        return [];
      }
      throw error;
    }
  }

  /**
   * Sync a run's status from Apify or manually update
   */
  async syncRunStatus(
    runId: string,
    request: SyncRunStatusRequest = {}
  ): Promise<SyncRunStatusResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/executions/${runId}/sync-status`, {
      method: 'POST',
      body: JSON.stringify(request),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Cleanup stale run statuses
   */
  async cleanupStaleStatuses(
    request: CleanupStaleStatusesRequest = {}
  ): Promise<CleanupStaleStatusesResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/executions/cleanup-stale-statuses`, {
      method: 'POST',
      body: JSON.stringify(request),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Sync all stale statuses (Admin endpoint)
   */
  async syncAllStatuses(request: SyncAllStatusesRequest = {}): Promise<SyncAllStatusesResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/executions/sync-all-statuses`, {
      method: 'POST',
      body: JSON.stringify(request),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Rehydrate results from an existing Apify run
   */
  async retrieveExistingRun(request: ExistingRunRequest): Promise<ExistingRunResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/tasks/existing`, {
      method: 'POST',
      body: JSON.stringify(request),
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Backfill missing data for a completed run that's missing dataset fields
   */
  async backfillRun(runId: string, force: boolean = false): Promise<BackfillResponse> {
    if (!this.baseUrl) {
      throw new APIError('Data Collection API URL not configured', 500, 'Configuration Error');
    }

    const response = await authenticatedFetch(`${this.baseUrl}/executions/${runId}/backfill`, {
      method: 'POST',
      body: JSON.stringify({ force }),
      timeout: this.timeout
    });

    return response.json();
  }
}

export const dataCollectionAPI = new DataCollectionAPI();
